import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMeasure } from 'context/measure';
import { getFocusedAgeLevel } from 'helpers/dimension';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import InfoModal from 'components/Modals/InfoModal';
import { SectionCollapse } from 'components/SectionCollapse';
import { SectionWithBorder } from 'components/Measure/View/Styled';
import {
  useValidations,
  useServerFieldError,
  getSubsectionStatus
} from 'components/Measure/View/ValidationsContext';

import {
  Select,
  SelectValue,
  TextArea
} from '@teachstonellc/component-library';

import {
  ThumbsUp,
  TrendingUp
} from '@teachstonellc/component-library/dist/icons';

import {
  updateObservation,
  useObservationSuggestions,
  useUpdateObservation,
  UpdateObservationParams
} from 'actions/envScales/observations';

import {
  getDimension,
  getIndicator,
  getDimensionOptions,
  getIndicatorOptions,
  getFirstIndicator
} from './utils';

const ICON_SIZE = 20;

const requiredId = yup.number().required();
const optionalId = yup.number().nullable();
const requiredText = yup.string().required();

function toId(value: SelectValue) {
  return value === null || value === '' ? null : Number(value);
}

interface ClassroomExamplesSectionProps {
  /** When `true`, top border radius is applied to the section. */
  attached?: boolean;
}

function ClassroomExamplesSection({ attached }: ClassroomExamplesSectionProps) {
  const { assessment, ageLevel } = useMeasure();
  const observationId = assessment.observation!.id;
  const isInformal = assessment.informal;
  const { sections, errors, resolvedPaths, showErrorStatus } = useValidations();

  const focusedAgeLevel = useMemo(
    () => getFocusedAgeLevel(ageLevel, assessment),
    [ageLevel, assessment]
  );

  const [touched, setTouched] = useState(false);
  const [modalConfirmed, setModalConfirmed] = useState(false);
  const [strengthDimensionId, setStrengthDimensionId] = useState<number | null>(
    null
  );
  const [strengthIndicatorId, setStrengthIndicatorId] = useState<number | null>(
    null
  );
  const [growthDimensionId, setGrowthDimensionId] = useState<number | null>(
    null
  );
  const [growthIndicatorId, setGrowthIndicatorId] = useState<number | null>(
    null
  );
  const [strengthText, setStrengthText] = useState('');
  const [growthText, setGrowthText] = useState('');

  const { data: suggestions, isPending } =
    useObservationSuggestions(observationId);
  const { mutate: saveObservation } = useUpdateObservation(observationId);

  // Seeds the form from the server's copy, and re-seeds if a newer response
  // arrives. Stops as soon as the observer edits anything: their in-progress
  // input outranks the server's copy.
  useEffect(() => {
    if (!suggestions || touched) {
      return;
    }

    setStrengthDimensionId(suggestions.selected_strength_dimension_id ?? null);
    setStrengthIndicatorId(suggestions.selected_strength_indicator_id ?? null);
    setGrowthDimensionId(suggestions.selected_improve_dimension_id ?? null);
    setGrowthIndicatorId(suggestions.selected_improve_indicator_id ?? null);
    setStrengthText(suggestions.strength_text || '');
    setGrowthText(suggestions.improve_text || '');
  }, [suggestions, touched]);

  // Edits the debounce has not written yet. Kept in a ref so they can still be
  // flushed if the observer leaves the page inside the debounce window.
  const pendingSave = useRef<UpdateObservationParams | null>(null);

  useEffect(() => {
    if (!touched) {
      return;
    }

    pendingSave.current = {
      selected_strength_dimension_id: strengthDimensionId,
      selected_strength_indicator_id: strengthIndicatorId,
      selected_improve_dimension_id: growthDimensionId,
      selected_improve_indicator_id: growthIndicatorId,
      strength_text: strengthText,
      improve_text: growthText
    };
  }, [
    touched,
    strengthDimensionId,
    strengthIndicatorId,
    growthDimensionId,
    growthIndicatorId,
    strengthText,
    growthText
  ]);

  useDebouncedEffect(
    () => {
      if (!pendingSave.current) {
        return;
      }

      saveObservation(pendingSave.current);
      pendingSave.current = null;
    },
    1000,
    [
      touched,
      strengthDimensionId,
      strengthIndicatorId,
      growthDimensionId,
      growthIndicatorId,
      strengthText,
      growthText,
      saveObservation
    ]
  );

  useEffect(() => {
    return () => {
      if (!pendingSave.current) {
        return;
      }

      // Unmounting, so the mutation's subscriber is already gone -- send the
      // request directly rather than through the hook. Nothing can react to
      // the outcome after unmount, but the promise still needs a handler so
      // a failed save doesn't surface as an unhandled rejection.
      updateObservation(observationId, pendingSave.current).catch(() => {});
      pendingSave.current = null;
    };
  }, [observationId]);

  function handleDimensionChange(
    type: 'strength' | 'growth',
    dimensionId: number | null
  ) {
    // Informal observations get no suggestions and may leave the indicator
    // blank, so changing the dimension clears it rather than pre-filling it.
    const indicatorId = isInformal
      ? null
      : (getFirstIndicator(ageLevel, dimensionId)?.id ?? null);

    if (type === 'strength') {
      setStrengthDimensionId(dimensionId);
      setStrengthIndicatorId(indicatorId);
    } else {
      setGrowthDimensionId(dimensionId);
      setGrowthIndicatorId(indicatorId);
    }

    setTouched(true);
  }

  function handleModalConfirmed() {
    setModalConfirmed(true);
    saveObservation({ scores_changed: false });
  }

  function buildInfoModalContent() {
    const strengthDimension = getDimension(ageLevel, strengthDimensionId)?.name;
    const strengthIndicator = getIndicator(
      ageLevel,
      strengthDimensionId,
      strengthIndicatorId
    )?.name;
    const growthDimension = getDimension(ageLevel, growthDimensionId)?.name;
    const growthIndicator = getIndicator(
      ageLevel,
      growthDimensionId,
      growthIndicatorId
    )?.name;

    return (
      <div>
        <p>{i18n.ft(messages.scoresChangedContent)}</p>
        <p>{i18n.ft(messages.currentSelection)}</p>
        <p>
          {i18n.ft(messages.strength) +
            ': ' +
            strengthDimension +
            ' - ' +
            strengthIndicator}
        </p>
        <p>
          {i18n.ft(messages.growth) +
            ': ' +
            growthDimension +
            ' - ' +
            growthIndicator}
        </p>
      </div>
    );
  }

  const cyclesComplete = Boolean(suggestions?.cycles_complete);

  // Informal scores may legitimately be left blank, so the form never waits
  // on complete cycles.
  const fieldsDisabled = !isInformal && !cyclesComplete;

  const showModal =
    !isInformal &&
    !modalConfirmed &&
    Boolean(suggestions?.scores_changed) &&
    !!assessment.observation?.selected_improve_dimension_id &&
    !!assessment.observation?.selected_strength_indicator_id;

  const strengthDescription = isInformal
    ? i18n.ft(messages.strengthExampleDesc)
    : i18n.ft(messages.strengthExampleDescFormal);
  const growthDescription = isInformal
    ? i18n.ft(messages.growthExampleDesc)
    : i18n.ft(messages.growthExampleDescFormal);

  const suggestedLabel = i18n.ft(messages.suggested);
  const suggestedStrengthDimensionId = isInformal
    ? null
    : (suggestions?.recommended_strength_dimension_id ?? null);
  const suggestedStrengthIndicatorId = isInformal
    ? null
    : (suggestions?.recommended_strength_indicator_id ?? null);
  const suggestedGrowthDimensionId = isInformal
    ? null
    : (suggestions?.recommended_improve_dimension_id ?? null);
  const suggestedGrowthIndicatorId = isInformal
    ? null
    : (suggestions?.recommended_improve_indicator_id ?? null);

  // Only the dimensions of focus are offered, since the others were never
  // scored. The lookups above stay on the full age level so a dimension saved
  // before the focus changed still resolves to a name.
  const strengthDimensions = getDimensionOptions(
    focusedAgeLevel,
    suggestedStrengthDimensionId,
    suggestedLabel
  );
  const growthDimensions = getDimensionOptions(
    focusedAgeLevel,
    suggestedGrowthDimensionId,
    suggestedLabel
  );
  const strengthIndicators = getIndicatorOptions(
    ageLevel,
    strengthDimensionId,
    suggestedStrengthIndicatorId,
    suggestedLabel
  );
  const growthIndicators = getIndicatorOptions(
    ageLevel,
    growthDimensionId,
    suggestedGrowthIndicatorId,
    suggestedLabel
  );

  const completed =
    Boolean(strengthText) &&
    Boolean(growthText) &&
    strengthDimensionId !== null &&
    strengthIndicatorId !== null &&
    growthDimensionId !== null &&
    growthIndicatorId !== null;

  const status = isInformal
    ? null
    : !showErrorStatus
      ? completed
        ? 'completed'
        : 'pending'
      : sections
        ? getSubsectionStatus(
            sections.evidence,
            'strength_growth',
            showErrorStatus,
            { errors, resolvedPaths, prefix: 'evidence.strength_growth' }
          )
        : 'pending';

  const strengthDimensionError = useServerFieldError(
    'evidence.strength_growth.selected_strength_dimension_id',
    strengthDimensionId,
    isInformal ? optionalId : requiredId
  );
  const strengthIndicatorError = useServerFieldError(
    'evidence.strength_growth.selected_strength_indicator_id',
    strengthIndicatorId,
    isInformal ? optionalId : requiredId
  );
  const growthDimensionError = useServerFieldError(
    'evidence.strength_growth.selected_improve_dimension_id',
    growthDimensionId,
    isInformal ? optionalId : requiredId
  );
  const growthIndicatorError = useServerFieldError(
    'evidence.strength_growth.selected_improve_indicator_id',
    growthIndicatorId,
    isInformal ? optionalId : requiredId
  );
  const strengthTextError = useServerFieldError(
    'evidence.strength_growth.strength_text',
    strengthText,
    isInformal ? yup.string() : requiredText
  );
  const growthTextError = useServerFieldError(
    'evidence.strength_growth.improve_text',
    growthText,
    isInformal ? yup.string() : requiredText
  );

  return (
    <>
      <InfoModal
        title={i18n.ft(messages.scoresChangedTitle)}
        message={buildInfoModalContent()}
        icon="info circle"
        isOpen={showModal}
        confirmButton={i18n.ft(messages.scoresChangedConfirmation)}
        onConfirm={handleModalConfirmed}
      />

      <SectionCollapse
        title={i18n.ft(messages.header)}
        status={status}
        attached={attached}
        closeOthersOnOpen
      >
        {isPending ? (
          <div className="py-8 text-center">
            <i
              aria-label="loading"
              role="status"
              className="fa-solid fa-spinner animate-spin text-xl"
            />
          </div>
        ) : (
          <>
            {!isInformal && !cyclesComplete && (
              <div className="text-red-500 py-2">
                {i18n.ft(messages.errors.missingCyclesInformation)}
              </div>
            )}

            <SectionWithBorder>
              <div className="flex items-center gap-2 text-lg leading-7 font-bold">
                {i18n.ft(messages.strengthExample)}
                <ThumbsUp size={ICON_SIZE} className="text-scale-blue" />
              </div>
              <div className="mt-2">{strengthDescription}</div>

              <fieldset className="mt-6 p-0 min-w-0">
                <legend className="sr-only">
                  {i18n.ft(messages.strengthExample)}
                </legend>
                <div className="grid gap-6 lg:grid-cols-2 lg:gap-4">
                  <Select
                    label={i18n.ft(messages.dimension)}
                    placeholder={i18n.ft(messages.dimensionPlaceholder)}
                    options={strengthDimensions}
                    disabled={fieldsDisabled}
                    value={strengthDimensionId}
                    error={strengthDimensionError?.message}
                    onChange={value =>
                      handleDimensionChange('strength', toId(value))
                    }
                  />
                  <Select
                    label={i18n.ft(messages.indicator)}
                    placeholder={i18n.ft(messages.indicatorPlaceholder)}
                    options={strengthIndicators}
                    disabled={fieldsDisabled}
                    value={strengthIndicatorId}
                    error={strengthIndicatorError?.message}
                    onChange={value => {
                      setStrengthIndicatorId(toId(value));
                      setTouched(true);
                    }}
                  />
                </div>

                <div className="mt-4">
                  <TextArea
                    rows={3}
                    showLabel={false}
                    label={i18n.ft(messages.strengthExample)}
                    aria-label={i18n.ft(messages.strengthExample)}
                    placeholder={i18n.ft(messages.textPlaceholder)}
                    disabled={fieldsDisabled}
                    value={strengthText}
                    error={strengthTextError?.message}
                    onChange={event => {
                      setStrengthText(event.target.value);
                      setTouched(true);
                    }}
                  />
                </div>
              </fieldset>
            </SectionWithBorder>

            <SectionWithBorder className="mt-6">
              <div className="flex items-center gap-2 text-lg leading-7 font-bold">
                {i18n.ft(messages.growthExample)}
                <TrendingUp size={ICON_SIZE} className="text-scale-blue" />
              </div>
              <div className="mt-2">{growthDescription}</div>

              <fieldset className="mt-6 p-0 min-w-0">
                <legend className="sr-only">
                  {i18n.ft(messages.growthExample)}
                </legend>
                <div className="grid gap-6 lg:grid-cols-2 lg:gap-4">
                  <Select
                    label={i18n.ft(messages.dimension)}
                    placeholder={i18n.ft(messages.dimensionPlaceholder)}
                    options={growthDimensions}
                    disabled={fieldsDisabled}
                    value={growthDimensionId}
                    error={growthDimensionError?.message}
                    onChange={value =>
                      handleDimensionChange('growth', toId(value))
                    }
                  />
                  <Select
                    label={i18n.ft(messages.indicator)}
                    placeholder={i18n.ft(messages.indicatorPlaceholder)}
                    options={growthIndicators}
                    disabled={fieldsDisabled}
                    value={growthIndicatorId}
                    error={growthIndicatorError?.message}
                    onChange={value => {
                      setGrowthIndicatorId(toId(value));
                      setTouched(true);
                    }}
                  />
                </div>

                <div className="mt-4">
                  <TextArea
                    rows={3}
                    showLabel={false}
                    label={i18n.ft(messages.growthExample)}
                    aria-label={i18n.ft(messages.growthExample)}
                    placeholder={i18n.ft(messages.textPlaceholder)}
                    disabled={fieldsDisabled}
                    value={growthText}
                    error={growthTextError?.message}
                    onChange={event => {
                      setGrowthText(event.target.value);
                      setTouched(true);
                    }}
                  />
                </div>
              </fieldset>
            </SectionWithBorder>
          </>
        )}
      </SectionCollapse>
    </>
  );
}

export default ClassroomExamplesSection;
